import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useV2GetProjectVariableByIdQuery } from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import UnsavedChangesModal from "@components/UnsavedChangesModal/UnsavedChangesModal";
import { UserDefinedFieldForm } from "../UserDefinedFieldForm/UserDefinedFieldForm";
import { useUserDefinedFieldMutations } from "../api/useUserDefinedFieldMutations";
import type { UserDefinedFieldFormValues } from "../types/UserDefinedField";

const FORM_ID = "user-defined-field-form";

export const Edit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useV2GetProjectVariableByIdQuery({
    id: Number(id),
  });
  const { update } = useUserDefinedFieldMutations();
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<UserDefinedFieldFormValues>();

  const initial = useMemo<UserDefinedFieldFormValues | null>(() => {
    if (!data?.data) return null;
    const record: any = data.data;
    return {
      label: record.attributes.label ?? "",
      name: record.attributes.name ?? "",
      variableType: record.attributes["variable-type"] ?? "text",
      context: record.attributes["variable-context"] ?? "project",
      required: !!record.attributes.required,
      minimum: record.attributes.minimum ?? null,
      maximum: record.attributes.maximum ?? null,
      selectOptions: record.attributes["select-options"] ?? [],
    };
  }, [data]);

  if (isLoading || !initial) return <ScopeStackSpinner />;

  const recordName =
    (data?.data as any)?.attributes?.label ||
    (data?.data as any)?.attributes?.name ||
    "Field";

  const handleSubmit = async (values: UserDefinedFieldFormValues) => {
    setSubmitting(true);
    try {
      await update(id!, values);
      markSaved(values, "Field updated.");
    } catch (err) {
      console.error("Failed to update user-defined field", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) setShowUnsavedModal(true);
    else navigate("..");
  };

  return (
    <>
      <AdminFormPage
        title={`Edit ${recordName}`}
        formId={FORM_ID}
        onCancel={handleCancel}
        isSubmitting={submitting}
      >
        <UserDefinedFieldForm
          key={formKey}
          id={FORM_ID}
          initialValues={savedInitial ?? initial}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          onDirtyChange={setIsDirty}
        />
      </AdminFormPage>
      <UnsavedChangesModal
        show={showUnsavedModal}
        handleCancel={() => setShowUnsavedModal(false)}
        handleDontSave={() => {
          setShowUnsavedModal(false);
          navigate("..");
        }}
        handleSave={() => {
          setShowUnsavedModal(false);
          (
            document.getElementById(FORM_ID) as HTMLFormElement | null
          )?.requestSubmit();
        }}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
